<script>
import { getMainDisplayList, createMainDisplay, updateMainDisplay, deleteMainDisplay } from '@/api/mainDisplay.js'
import { getProductList } from '@/api/product.js'
import { getPresignedUrl, uploadAttachment } from '@/api/attachment.js'
import { summaryText } from '@/constants/imageSpecs.js'

const areaMeta = {
  MAIN_BANNER: { label: '메인 배너', icon: 'mdi-image-area' },
  MAIN_PRODUCT: { label: '메인 상품', icon: 'mdi-package-variant' },
  PROMOTION_BANNER: { label: '프로모션 배너', icon: 'mdi-view-carousel-outline' },
}

export default {
  name: 'MainDisplayPage',
  data() {
    return {
      activeArea: 'MAIN_BANNER',
      items: [],
      products: [],
      registeredMainProductKeys: [],
      productSearchKeyword: '',
      productLoading: false,
      productSearchTimer: null,
      keyword: '',
      loading: false,
      saving: false,
      dialog: false,
      editingCode: '',
      imagePreviewUrl: '',
      form: this.emptyForm('MAIN_BANNER'),
      areaOptions: Object.entries(areaMeta).map(([value, meta]) => ({ value, title: meta.label })),
      ynOptions: [
        { title: 'Y', value: 'Y' },
        { title: 'N', value: 'N' },
      ],
      linkTypeOptions: [
        { title: '없음', value: 'NONE' },
        { title: 'URL', value: 'URL' },
        { title: '상품', value: 'PRODUCT' },
      ],
    }
  },
  computed: {
    imageSpec() {
      const map = { MAIN_BANNER: 'MALL_MAIN_BANNER', MAIN_PRODUCT: 'MALL_MAIN_PRODUCT', PROMOTION_BANNER: 'MALL_MAIN_BANNER' }
      return summaryText(map[this.activeArea] || 'MALL_MAIN_BANNER')
    },
    activeMeta() {
      return areaMeta[this.activeArea] || areaMeta.MAIN_BANNER
    },
    productOptions() {
      return this.products
        .filter(product => this.isProductSelectable(product))
        .map(p => ({
          title: `${p.branchName ? `${p.branchName} / ` : ''}${p.productName} (${p.branchProductCode || p.productCode})`,
          value: p.branchProductCode || p.productCode,
        }))
    },
  },
  mounted() {
    this.fetchProducts()
    this.fetchRegisteredMainProducts()
    this.fetchItems()
  },
  methods: {
    emptyForm(area) {
      return {
        displayArea: area,
        productSelectionKey: '',
        productCode: '',
        branchProductCode: '',
        title: '',
        subtitle: '',
        description: '',
        mediaUrl: '',
        storageKey: '',
        linkType: 'NONE',
        linkUrl: '',
        exposeYn: 'Y',
        alwaysDisplayYn: 'Y',
        exposeStartDt: '',
        exposeEndDt: '',
        exposeOrder: 0,
      }
    },
    async fetchProducts(keyword = '') {
      this.productLoading = true
      try {
        const res = await getProductList({
          page: 1,
          size: 30,
          includeBranchProducts: 'Y',
          viewYn: 'Y',
          keyword: keyword || undefined,
        })
        const payload = res.data?.data
        this.products = payload?.items || payload?.content || []
        this.ensureSelectedProductOption()
      } finally {
        this.productLoading = false
      }
    },
    searchProducts(keyword) {
      this.productSearchKeyword = keyword || ''
      window.clearTimeout(this.productSearchTimer)
      this.productSearchTimer = window.setTimeout(() => {
        this.fetchProducts(this.productSearchKeyword)
      }, 250)
    },
    ensureSelectedProductOption() {
      const selectionKey = this.form?.productSelectionKey
      if (!selectionKey || this.findProduct(selectionKey)) return
      if (!this.form.productCode) return

      this.products = [
        {
          productCode: this.form.productCode,
          branchProductCode: this.form.branchProductCode,
          branchName: '',
          productName: this.form.title || this.form.productCode,
        },
        ...this.products,
      ]
    },
    async fetchItems() {
      this.loading = true
      try {
        const res = await getMainDisplayList({ displayArea: this.activeArea, keyword: this.keyword })
        this.items = res.data?.items || []
        await this.applyPreviewUrls()
      } finally {
        this.loading = false
      }
    },
    async fetchRegisteredMainProducts() {
      const res = await getMainDisplayList({ displayArea: 'MAIN_PRODUCT' })
      this.registeredMainProductKeys = (res.data?.items || [])
        .map(item => this.mainProductKey(item))
        .filter(Boolean)
    },
    async applyPreviewUrls() {
      await Promise.all(this.items.map(async item => {
        item.previewUrl = await this.resolveImageUrl(item.storageKey, item.mediaUrl)
      }))
    },
    async resolveImageUrl(storageKey, fallbackUrl) {
      const objectKey = storageKey || this.extractS3ObjectKey(fallbackUrl)
      if (!objectKey) return fallbackUrl || ''
      try {
        const res = await getPresignedUrl(objectKey)
        if (res.data?.code === 0 && res.data?.presignedUrl) {
          return res.data.presignedUrl
        }
      } catch (e) {
        // Fall back to stored URL if presigned lookup fails.
      }
      return fallbackUrl || ''
    },
    extractS3ObjectKey(url) {
      if (!url) return ''
      const marker = '.amazonaws.com/'
      const markerIndex = url.indexOf(marker)
      if (markerIndex < 0) return ''
      return url.substring(markerIndex + marker.length)
    },
    changeArea(area) {
      this.activeArea = area
      this.keyword = ''
      this.fetchItems()
      if (area === 'MAIN_PRODUCT') {
        this.fetchRegisteredMainProducts()
      }
    },
    handleFormAreaChange(area) {
      if (area === 'MAIN_PRODUCT') {
        this.form.linkType = 'PRODUCT'
        if (this.form.productSelectionKey) {
          this.applySelectedProduct(this.form.productSelectionKey)
        }
      }
    },
    findProduct(selectionKey) {
      return this.products.find(product => (product.branchProductCode || product.productCode) === selectionKey)
    },
    mainProductKey(item) {
      return item?.branchProductCode || item?.productCode || ''
    },
    isProductSelectable(product) {
      const key = this.mainProductKey(product)
      if (!key) return false
      if (this.editingCode && key === this.form.productSelectionKey) {
        return true
      }
      return !this.registeredMainProductKeys.includes(key)
    },
    productDetailLink(productCode, branchProductCode) {
      if (!productCode) return ''
      const branchQuery = branchProductCode ? `&branchProductCode=${encodeURIComponent(branchProductCode)}` : ''
      return `/product-detail?productCode=${encodeURIComponent(productCode)}${branchQuery}`
    },
    applySelectedProduct(selectionKey) {
      if (!selectionKey) {
        if (this.form.displayArea === 'MAIN_PRODUCT') {
          this.form.productCode = ''
          this.form.branchProductCode = ''
          this.form.linkType = 'PRODUCT'
          this.form.linkUrl = ''
        }
        return
      }
      const selectedProduct = this.findProduct(selectionKey)
      if (!selectedProduct) return
      this.form.productCode = selectedProduct.productCode
      this.form.branchProductCode = selectedProduct.branchProductCode || ''
      this.form.title = selectedProduct?.productName || this.form.title
      this.form.linkType = 'PRODUCT'
      this.form.linkUrl = this.productDetailLink(selectedProduct.productCode, selectedProduct.branchProductCode)
    },
    openCreate() {
      this.editingCode = ''
      this.form = this.emptyForm(this.activeArea)
      this.productSearchKeyword = ''
      if (this.form.displayArea === 'MAIN_PRODUCT') {
        this.form.linkType = 'PRODUCT'
        this.fetchRegisteredMainProducts()
      }
      this.imagePreviewUrl = ''
      this.dialog = true
    },
    openEdit(item) {
      this.editingCode = item.mainAssetCode
      this.form = {
        displayArea: item.displayArea,
        productSelectionKey: item.branchProductCode || item.productCode || '',
        productCode: item.productCode || '',
        branchProductCode: item.branchProductCode || '',
        title: item.title || '',
        subtitle: item.subtitle || '',
        description: item.description || '',
        mediaUrl: item.mediaUrl || '',
        storageKey: item.storageKey || '',
        linkType: item.linkType || 'NONE',
        linkUrl: item.linkUrl || '',
        exposeYn: item.exposeYn || 'Y',
        alwaysDisplayYn: item.alwaysDisplayYn || 'Y',
        exposeStartDt: this.toInputDateTime(item.exposeStartDt),
        exposeEndDt: this.toInputDateTime(item.exposeEndDt),
        exposeOrder: item.exposeOrder || 0,
      }
      this.imagePreviewUrl = item.previewUrl || item.mediaUrl || ''
      this.productSearchKeyword = item.productName || item.title || ''
      this.ensureSelectedProductOption()
      this.dialog = true
    },
    toInputDateTime(value) {
      if (!value) return ''
      return String(value).slice(0, 16)
    },
    toApiDateTime(value) {
      return value ? `${value}:00` : null
    },
    async uploadImage(event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      const path = `goldnara/main/${yyyy}/${mm}/${dd}/${this.form.displayArea}/`
      const res = await uploadAttachment(path, file)
      if (res.data?.code === 0) {
        this.form.storageKey = res.data.objectKey
        this.form.mediaUrl = res.data.url
        this.imagePreviewUrl = await this.resolveImageUrl(res.data.objectKey, res.data.url)
      }
    },
    buildPayload() {
      const { productSelectionKey, ...form } = this.form
      return {
        ...form,
        productCode: form.displayArea === 'MAIN_PRODUCT' || form.linkType === 'PRODUCT' ? form.productCode : '',
        branchProductCode: form.displayArea === 'MAIN_PRODUCT' || form.linkType === 'PRODUCT' ? form.branchProductCode : '',
        mediaUrl: form.mediaUrl || null,
        storageKey: form.storageKey || null,
        exposeStartDt: form.alwaysDisplayYn === 'Y' ? null : this.toApiDateTime(form.exposeStartDt),
        exposeEndDt: form.alwaysDisplayYn === 'Y' ? null : this.toApiDateTime(form.exposeEndDt),
      }
    },
    async save() {
      this.saving = true
      try {
        const payload = this.buildPayload()
        let res
        if (this.editingCode) {
          res = await updateMainDisplay(this.editingCode, payload)
        } else {
          res = await createMainDisplay(payload)
        }
        if (res.data?.code !== 0) {
          window.alert(res.data?.message || '저장에 실패했습니다.')
          return
        }
        this.dialog = false
        await this.fetchRegisteredMainProducts()
        await this.fetchItems()
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      if (!window.confirm('삭제하시겠습니까?')) return
      await deleteMainDisplay(item.mainAssetCode)
      await this.fetchRegisteredMainProducts()
      await this.fetchItems()
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">메인화면 관리</h1>
        <p class="text-body-2 text-grey-lighten-1">메인 배너, 메인 상품, 프로모션 배너를 관리합니다.</p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">등록</v-btn>
    </div>

    <v-card class="mb-4" variant="outlined">
      <v-card-text>
        <div class="d-flex ga-2 flex-wrap mb-4">
          <v-btn
            v-for="area in areaOptions"
            :key="area.value"
            :color="activeArea === area.value ? 'primary' : undefined"
            :variant="activeArea === area.value ? 'flat' : 'outlined'"
            @click="changeArea(area.value)"
          >
            {{ area.title }}
          </v-btn>
        </div>
        <div class="d-flex ga-2">
          <v-text-field v-model="keyword" label="검색" variant="outlined" density="compact" hide-details @keyup.enter="fetchItems" />
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="fetchItems">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="keyword=''; fetchItems()">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card variant="outlined">
      <v-table>
        <thead>
          <tr>
            <th>순서</th>
            <th>이미지</th>
            <th>제목</th>
            <th>연결 상품</th>
            <th>노출</th>
            <th>상시</th>
            <th>기간</th>
            <th class="text-right">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!items.length">
            <td colspan="8" class="text-center py-8">등록된 항목이 없습니다.</td>
          </tr>
          <tr v-for="item in items" :key="item.mainAssetCode">
            <td>{{ item.exposeOrder }}</td>
            <td>
              <v-img
                v-if="item.previewUrl"
                :src="item.previewUrl"
                width="120"
                height="40"
                cover
                rounded
                class="border"
              />
              <span v-else class="text-caption text-grey">-</span>
            </td>
            <td>
              <div class="font-weight-bold">{{ item.title || '-' }}</div>
              <div class="text-caption text-grey">{{ item.subtitle }}</div>
            </td>
            <td>
              <div>{{ item.productName || item.productCode || '-' }}</div>
              <div v-if="item.branchName || item.branchProductCode" class="text-caption text-grey">
                {{ item.branchName || '-' }} / {{ item.branchProductCode }}
              </div>
            </td>
            <td>{{ item.exposeYn }}</td>
            <td>{{ item.alwaysDisplayYn }}</td>
            <td>{{ item.alwaysDisplayYn === 'Y' ? '상시' : `${item.exposeStartDt || ''} ~ ${item.exposeEndDt || ''}` }}</td>
            <td class="text-right">
              <v-btn icon="mdi-pencil" size="small" variant="text" @click="openEdit(item)" />
              <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click="remove(item)" />
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="760">
      <v-card>
        <v-card-title>{{ editingCode ? '수정' : '등록' }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select v-model="form.displayArea" :items="areaOptions" label="노출 영역" variant="outlined" density="compact" @update:model-value="handleFormAreaChange" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="form.exposeOrder" type="number" label="노출 순서" variant="outlined" density="compact" />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="form.exposeYn" :items="ynOptions" label="노출 여부" variant="outlined" density="compact" />
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-if="form.displayArea === 'MAIN_PRODUCT' || form.linkType === 'PRODUCT'"
                v-model="form.productSelectionKey"
                v-model:search="productSearchKeyword"
                :items="productOptions"
                :loading="productLoading"
                label="연결 상품"
                placeholder="상품명, 지점명, 상품코드로 검색"
                variant="outlined"
                density="compact"
                clearable
                hide-selected
                no-filter
                no-data-text="검색된 상품이 없습니다."
                @update:search="searchProducts"
                @update:model-value="applySelectedProduct"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.title" label="제목" variant="outlined" density="compact" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.subtitle" label="부제목" variant="outlined" density="compact" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="form.description" label="설명" variant="outlined" rows="3" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="form.linkType" :items="linkTypeOptions" label="링크 유형" variant="outlined" density="compact" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.linkUrl" label="링크 URL" variant="outlined" density="compact" :disabled="form.linkType !== 'URL'" />
            </v-col>
            <v-col cols="12">
              <div class="d-flex align-center ga-3">
                <v-btn variant="outlined" prepend-icon="mdi-upload" @click="$refs.imageInput.click()">이미지 업로드</v-btn>
                <input ref="imageInput" type="file" accept="image/*" class="d-none" @change="uploadImage">
                <span class="text-caption text-grey">{{ form.storageKey || form.mediaUrl || '업로드된 이미지 없음' }}</span>
              </div>
              <div class="text-caption text-grey mt-2">
                {{ imageSpec }}
                <router-link to="/media-hub/image-guide" class="ml-1">제작 가이드</router-link>
              </div>
            </v-col>
            <v-col v-if="imagePreviewUrl" cols="12">
              <v-img
                :src="imagePreviewUrl"
                max-height="180"
                cover
                rounded
                class="border"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="form.alwaysDisplayYn" :items="ynOptions" label="상시 노출" variant="outlined" density="compact" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.exposeStartDt" type="datetime-local" label="노출 시작" variant="outlined" density="compact" :disabled="form.alwaysDisplayYn === 'Y'" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.exposeEndDt" type="datetime-local" label="노출 종료" variant="outlined" density="compact" :disabled="form.alwaysDisplayYn === 'Y'" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog=false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
